import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Spin } from "antd";
import Snowfall from "react-snowfall";

import AppCarousel from "./AppCarousel";
import { useInfoUser } from "../../store/UserStore";
import { useImageLoader } from "../customHook/ImageLoader";
import FireworksCanvas from "../../pages/adminSetting/WelcomeManagement/FireworksCanvas";
import { useAudioLoader } from "../customHook/AudioLoader";
import boom from "../../assets/boom.mp3";

export const EFFECT_REGISTRY = {
  snow: {
    component: Snowfall,
    props: { snowflakeCount: 300 },
    zIndex: 10,
  },
  firework: {
    component: FireworksCanvas,
    props: {},
    zIndex: 10,
  },
};

/* ================= SLIDE BACKGROUND ================= */
const SlideBackground = React.memo(
  ({ backgroundId, backgroundColor, background, children }) => {
    const { imageUrl } = useImageLoader(backgroundId);
    const lastImageRef = useRef(null);
    if (imageUrl) {
      lastImageRef.current = imageUrl;
    }

    const style = useMemo(
      () => ({
        backgroundColor: backgroundColor || background,
        backgroundImage: lastImageRef.current
          ? `url(${lastImageRef.current})`
          : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }),
      [backgroundColor, imageUrl]
    );

    return (
      <div
        className="flex flex-col justify-center items-center gap-8 py-10 min-h-screen select-none"
        style={style}
      >
        {children}
      </div>
    );
  }
);

/* ================= SLIDE IMAGE ================= */
const SlideImage = React.memo(({ resourceId, width = 390 }) => {
  const { imageUrl, loading } = useImageLoader(resourceId);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ width, height: width }}
      >
        <Spin />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      width={width}
      alt=""
      className="select-none"
      draggable={false}
    />
  );
});

/* ================= MAIN ================= */
export default function WelcomeCarousel({ slides = [], musicResourceId }) {
  const { user } = useInfoUser();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const { audioUrl } = useAudioLoader(musicResourceId);
  const boomRef = useRef(null);
  const [activeSlideId, setActiveSlideId] = useState(slides[0]?.slideId);

  const activeSlide = useMemo(
    () => slides.find((s) => s.slideId === activeSlideId),
    [slides, activeSlideId]
  );

  const effectConfig = EFFECT_REGISTRY[activeSlide?.effect];
  const EffectComponent = effectConfig?.component;

  /* ================= LANGUAGE ================= */
  const lang = user?.language || "en";

  const resolveLang = useCallback(
    (slide) => {
      switch (lang) {
        case "vn":
          return {
            title: slide.titleVn || slide.titleEn,
            description: slide.descriptionVn || slide.descriptionEn,
          };
        case "kr":
          return {
            title: slide.titleKr || slide.titleEn,
            description: slide.descriptionKr || slide.descriptionEn,
          };
        default:
          return {
            title: slide.titleEn,
            description: slide.descriptionEn,
          };
      }
    },
    [lang]
  );

  /* ================= MAP DATA ================= */
  const carouselData = useMemo(() => {
    return [...slides].map((slide) => {
      const { title, description } = resolveLang(slide);

      return {
        key: slide.slideId,
        slide,
        title,
        description,
      };
    });
  }, [slides, resolveLang]);

  const toggleMusic = useCallback(() => {
    if (!audioRef.current) return;

    const isFirework = activeSlide?.effect === "firework";

    if (playing) {
      audioRef.current.pause();
      boomRef.current?.pause();
    } else {
      audioRef.current.volume = 0.5;
      audioRef.current.play();

      if (isFirework && boomRef.current) {
        boomRef.current.volume = 0.2;
        boomRef.current.currentTime = 0;
        boomRef.current.play().catch(() => {});
      }
    }

    setPlaying((p) => !p);
  }, [playing, activeSlide]);

  useEffect(() => {
    if (!activeSlideId && slides.length > 0) {
      setActiveSlideId(slides[0].slideId);
    }
  }, [slides, activeSlideId]);

  useEffect(() => {
    if (!playing) return;

    if (activeSlide?.effect !== "firework") {
      boomRef.current?.pause();
    } else {
      boomRef.current?.play().catch(() => {});
    }
  }, [activeSlide, playing]);

  /* ================= RENDER ================= */
  return (
    <div className="relative overflow-hidden w-full">
      <audio ref={boomRef} src={boom} loop preload="auto" />
      {audioUrl && <audio ref={audioRef} src={audioUrl} loop />}
      {audioUrl && (
        <button
          onClick={toggleMusic}
          className="absolute top-4 left-4 z-[1000] bg-white/40
           backdrop-blur-md px-4 py-2 rounded-full text-white
            text-sm shadow-md hover:bg-white/60 transition cursor-pointer"
        >
          {playing ? "🔊 Music On" : "🔇 Music Off"}{" "}
        </button>
      )}
      {EffectComponent && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: effectConfig.zIndex }}
        >
          <EffectComponent {...effectConfig.props} />
        </div>
      )}
      {/* CAROUSEL */}
      <AppCarousel
        data={carouselData}
        autoplay
        options={{ loop: true }}
        onSlideChange={(item) => {
          setActiveSlideId(item?.slide?.slideId);
        }}
        renderItem={({ slide, title, description }) => (
          <SlideBackground
            backgroundId={slide.backgroundImage}
            backgroundColor={slide.backgroundColor}
            background={slide.background}
          >
            <SlideImage resourceId={slide.centerImage} width={390} />

            <div className="flex flex-col items-center text-center gap-4 max-w-lg">
              <div
                className="flex items-center justify-center gap-2 drop-shadow whitespace-nowrap"
                style={{
                  color: slide.titleColor,
                  fontSize: slide.titleSize,
                  fontFamily: slide.titleFont,
                }}
              >
                {slide.leftIcon && <span>{slide.leftIcon}</span>}
                <b>{title}</b>
                {slide.rightIcon && <span>{slide.rightIcon}</span>}
              </div>

              <div
                className="leading-relaxed"
                style={{
                  color: slide.descColor,
                  fontSize: slide.descSize,
                  fontFamily: slide.descFont,
                }}
              >
                {description}
              </div>
            </div>
          </SlideBackground>
        )}
      />
    </div>
  );
}
